import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    SortableContext,
    arrayMove,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import * as action from '../../Action/Curation/Curation_Action';
import { usePermission } from '../../hooks/usePermission';
import { PERM } from '../../config/permissions';
import { CuratedCollectionStyles } from './CuratedCollection';

/**
 * Coming Soon — the upcoming-releases rail, shown as a tab on /curated.
 *
 * GET /admin/v1/curation/curate-collections?curation_type=coming_soon returns the
 * rail's `preview` (series scheduled to go live, in rank order). Dragging rows only
 * changes a local order; Save & Publish sends the whole order as `reorder` in one
 * PATCH, so the rail ends up exactly as shown here. Reset to default sends
 * `reset_order: true` instead, which drops every manual rank so the series with
 * the nearest publish date comes first again.
 */

export const CURATION_TYPE = 'coming_soon';

/** Unwraps the shared response envelope: { success, message, data, ... } → data. */
const unwrap = (res) => (res?.data !== undefined ? res.data : res?.responseDetails);

const readMessage = (res, fallback) => res?.responseMessage || res?.message || fallback;

const readError = (err, fallback) => {
    const details = Array.isArray(err?.details) && err.details.length > 0
        ? err.details.map((d) => d?.message || d).join('; ')
        : null;
    return details || readMessage(err, fallback);
};

const toRows = (rows) => (Array.isArray(rows) ? rows : [])
    .map((row) => ({
        seriesId: Number(row?.series_id),
        position: Number(row?.position),
        title: row?.series_title || row?.title || '',
        thumbnail: row?.series_thumbnail || row?.thumbnail || '',
        publishAt: row?.publish_at || null,
    }))
    .filter((r) => Number.isFinite(r.seriesId) && Number.isFinite(r.position))
    .sort((a, b) => a.position - b.position);

/** The PATCH body for an order: every series at its 1-based rank. */
export const buildReorderBody = (configId, order) => ({
    curation_type: CURATION_TYPE,
    curation_config_id: configId,
    reorder: order.map((seriesId, i) => ({ series_id: seriesId, position: i + 1 })),
});

/** The PATCH body that puts the rail back in its default order: nearest publish date first. */
export const buildResetBody = (configId) => ({
    curation_type: CURATION_TYPE,
    curation_config_id: configId,
    reset_order: true,
});

const isDate = (value) => Number.isFinite(Date.parse(value));

const formatDateTime = (value) => new Date(value).toLocaleString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit',
});

const Thumb = ({ src }) => (
    <span className="cc-thumb">
        <i className="fas fa-film"></i>
        {src && (
            <img
                src={src}
                alt=""
                loading="lazy"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
        )}
    </span>
);

const SortableRow = ({ seriesId, allowEdit, busy, moved, children }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
        useSortable({ id: seriesId, disabled: !allowEdit || busy });

    return (
        <div
            ref={setNodeRef}
            className={`cc-row${moved ? ' cs-row-moved' : ''}${isDragging ? ' is-dragging' : ''}`}
            style={{ transform: CSS.Transform.toString(transform), transition }}
        >
            {allowEdit && (
                <button
                    type="button"
                    className="cc-handle"
                    title="Drag to change the rank"
                    disabled={busy}
                    {...attributes}
                    {...listeners}
                >
                    <i className="fas fa-grip-vertical"></i>
                </button>
            )}
            {children}
        </div>
    );
};

const ComingSoon = ({ onNameChange }) => {
    const dispatch = useDispatch();
    const { canEdit } = usePermission();
    const allowEdit = canEdit(PERM.SERIES);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [resetting, setResetting] = useState(false);
    const busy = saving || resetting;
    // True when GET 404s: the route (or this curation_type) is not deployed yet.
    const [apiMissing, setApiMissing] = useState(false);

    const [configId, setConfigId] = useState(null);
    const [name, setName] = useState('');
    const [updatedAt, setUpdatedAt] = useState(null);
    const [rows, setRows] = useState([]);
    // Unsaved drags: series ids in their new order, or null.
    const [draft, setDraft] = useState(null);

    const applyData = useCallback((data) => {
        const id = Number(data?.id);
        setConfigId(data?.id != null && data.id !== '' && Number.isFinite(id) ? id : null);
        setName(data?.name || '');
        setUpdatedAt(data?.updated_at || null);
        setRows(toRows(data?.preview));
    }, []);

    const fetchData = useCallback((silent = false) => {
        setLoading(true);
        return dispatch(action.getCuration(CURATION_TYPE))
            .then((res) => {
                setApiMissing(false);
                applyData(unwrap(res));
            })
            .catch((err) => {
                if (err?.status === 404) setApiMissing(true);
                if (!silent) toast.error(readMessage(err, 'Failed to load Coming Soon.'), { toastId: 'cs-load' });
            })
            .finally(() => setLoading(false));
    }, [dispatch, applyData]);

    useEffect(() => { fetchData(); }, [fetchData]);

    useEffect(() => { if (name) onNameChange?.(name); }, [name, onNameChange]);

    // Leaving the page would lose the unsaved drags.
    useEffect(() => {
        if (!draft) return undefined;
        const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
        window.addEventListener('beforeunload', warn);
        return () => window.removeEventListener('beforeunload', warn);
    }, [draft]);

    const savedOrder = useMemo(() => rows.map((r) => r.seriesId), [rows]);
    const order = draft ?? savedOrder;
    const rowById = useMemo(() => new Map(rows.map((r) => [r.seriesId, r])), [rows]);
    const movedCount = draft ? draft.filter((id, i) => savedOrder[i] !== id).length : 0;

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    const handleDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) return;
        const next = arrayMove(order, order.indexOf(active.id), order.indexOf(over.id));
        // Dragging everything back to where it was leaves nothing to save.
        setDraft(next.every((id, i) => savedOrder[i] === id) ? null : next);
    };

    /** Sends one PATCH and shows the rail it answers with. The draft is kept if it fails. */
    const publish = (body, successMessage, errorFallback) => dispatch(action.patchCuration(body))
        .then((res) => {
            toast.success(successMessage);
            setDraft(null);
            const data = unwrap(res);
            if (data && typeof data === 'object' && Array.isArray(data.preview)) applyData(data);
            else fetchData(true);
        })
        .catch((err) => {
            toast.error(readError(err, errorFallback), { toastId: 'cs-save' });
        });

    /** Save & Publish: the whole order in one PATCH. The draft is kept if it fails. */
    const saveOrder = () => {
        if (!allowEdit) {
            toast.error("Opps! You don't have Permission.");
            return;
        }
        if (!draft) return;
        if (configId === null) {
            toast.error('The Coming Soon config is missing its id. Reload and try again.', { toastId: 'cs-save' });
            return;
        }
        setSaving(true);
        publish(buildReorderBody(configId, draft), 'Coming Soon order published.', 'Failed to update Coming Soon.')
            .finally(() => setSaving(false));
    };

    /** Reset to default: after a confirm, the server drops every manual rank (and any unsaved drags go too). */
    const resetOrder = () => {
        if (!allowEdit) {
            toast.error("Opps! You don't have Permission.");
            return;
        }
        if (configId === null) {
            toast.error('The Coming Soon config is missing its id. Reload and try again.', { toastId: 'cs-save' });
            return;
        }
        Swal.fire({
            title: 'Reset to default order?',
            text: 'This moves the rail back to its default order: the series with the nearest publish date shows first. '
                + (draft ? 'Your unsaved rank changes are discarded too.' : 'Any ranks you set by hand are lost.'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: 'var(--error-color)',
            cancelButtonColor: 'var(--border-color)',
            confirmButtonText: 'Yes, reset',
            customClass: { confirmButton: 'btn', cancelButton: 'btn' },
        }).then((result) => {
            if (!result.isConfirmed) return;
            setResetting(true);
            publish(buildResetBody(configId), 'Coming Soon order reset to default.', 'Failed to reset Coming Soon.')
                .finally(() => setResetting(false));
        });
    };

    return (
        <>
            <div className="cc-page cs-page">
                <div className="cc-toolbar">
                    <div className="cc-intro">
                        <h5>Upcoming Releases Rail</h5>
                        <p>
                            Series scheduled to go live. Drag rows to change their rank, then
                            Save &amp; Publish to make the new order live.
                        </p>
                    </div>

                    {allowEdit && (
                        <div className="cc-actions">
                            <button
                                type="button"
                                className="btn cc-btn-ghost cs-btn-danger"
                                onClick={resetOrder}
                                disabled={busy || loading || apiMissing}
                                title="Order by nearest publish date"
                            >
                                {resetting
                                    ? <><i className="fas fa-spinner fa-spin me-2"></i>Resetting...</>
                                    : <><i className="fas fa-rotate-left me-2"></i>Reset to default</>}
                            </button>
                            {draft && (
                                <>
                                    <button
                                        type="button"
                                        className="btn cc-btn-ghost"
                                        onClick={() => setDraft(null)}
                                        disabled={busy}
                                    >
                                        <i className="fas fa-undo me-2"></i>Discard
                                    </button>
                                    <button
                                        type="button"
                                        className="btn upgrade-btn"
                                        onClick={saveOrder}
                                        disabled={busy}
                                    >
                                        {saving
                                            ? <><i className="fas fa-spinner fa-spin me-2"></i>Saving...</>
                                            : <><i className="fas fa-save me-2"></i>Save &amp; Publish</>}
                                    </button>
                                </>
                            )}
                        </div>
                    )}
                </div>

                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border" style={{ color: 'var(--first-color)' }} role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : apiMissing ? (
                    <div className="cc-empty">
                        <i className="fas fa-plug-circle-xmark"></i>
                        <h5>This rail isn't available on the server yet</h5>
                        <p>
                            The API answered 404 for <code>/admin/v1/curation/curate-collections?curation_type={CURATION_TYPE}</code>.
                        </p>
                        <button type="button" className="btn cc-btn-ghost mt-3" onClick={() => fetchData()}>
                            <i className="fas fa-rotate-right me-2"></i>Try again
                        </button>
                    </div>
                ) : (
                    <>
                        {draft && (
                            <div className="cc-note">
                                <i className="fas fa-circle-exclamation me-2"></i>
                                <span>
                                    {movedCount} series changed rank. Click
                                    <strong className="mx-1">Save &amp; Publish</strong>
                                    to make the new order live, or Discard.
                                </span>
                            </div>
                        )}

                        <div className="cc-list">
                            {order.length === 0 ? (
                                <div className="cc-empty">
                                    <i className="fas fa-clock"></i>
                                    <h5>Nothing coming soon</h5>
                                    <p>Series scheduled to go live show up here.</p>
                                </div>
                            ) : (
                                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                                    <SortableContext items={order} strategy={verticalListSortingStrategy}>
                                        {order.map((seriesId, i) => {
                                            const row = rowById.get(seriesId);
                                            const title = row?.title || `Series #${seriesId}`;
                                            return (
                                                <SortableRow
                                                    key={seriesId}
                                                    seriesId={seriesId}
                                                    allowEdit={allowEdit}
                                                    busy={busy}
                                                    moved={draft !== null && savedOrder[i] !== seriesId}
                                                >
                                                    <span className="cc-position-badge">#{i + 1}</span>
                                                    <Thumb src={row?.thumbnail} />
                                                    <span className="cs-row-text">
                                                        <span className="cc-title" title={title}>{title}</span>
                                                        {isDate(row?.publishAt) && (
                                                            <small>Goes live {formatDateTime(row.publishAt)}</small>
                                                        )}
                                                    </span>
                                                </SortableRow>
                                            );
                                        })}
                                    </SortableContext>
                                </DndContext>
                            )}
                        </div>

                        {isDate(updatedAt) && <p className="cc-meta">Last saved {formatDateTime(updatedAt)}.</p>}
                    </>
                )}
            </div>

            <CuratedCollectionStyles />
            <style>{`
                .cs-row-text { flex: 1 1 auto; min-width: 0; display: flex; flex-direction: column; }
                .cs-row-text .cc-title { flex: none; }
                .cs-row-text small { font-size: 11.5px; color: var(--text-color); }
                .cs-page .cc-row.is-dragging {
                    border-color: var(--first-color); box-shadow: var(--shadow-glow);
                    position: relative; z-index: 2;
                }
                .cs-row-moved { border-style: dashed; border-color: var(--first-color); }
                .cs-page .cc-btn-ghost:hover:not(:disabled) { color: #fff; }
                .cs-page .cs-btn-danger:hover:not(:disabled) {
                    background: var(--error-color); border-color: var(--error-color);
                }
            `}</style>
        </>
    );
};

export default ComingSoon;
